import { Trans, useTranslation } from 'react-i18next';
import { differenceInDays, startOfDay } from 'date-fns';
import { useForm } from 'react-form-state-manager';

import { GetEventSettingsQuery as GetEventSettings, Permission } from '__generated__/graphql';

import { Event } from './helpers';
import { getServerErrors } from '../../../../common/helpers';
import ProtectedSection from '../../../Common/ProtectedSection';
import UI from '../../../../common/UI';
import useEditEventMutation from './useEditEventMutation';
import useLocale from '../../../../common/useLocale';
import useNavigationPrompt from '../../../../common/useNavigationPrompt';
import useNumberInput from '../../../../common/useNumberInput';
import usePaymentMethods, { PaymentMethod } from '../../../../common/usePaymentMethods';
import useProject from '../../../useProject';

type PaymentProfile = GetEventSettings['project']['payment_profiles'][0];

export interface EditEventPaymentSettingsFormProps {
  event: Event;
  paymentProfiles: PaymentProfile[];
}

const EditEventPaymentSettingsForm = ({
  event,
  paymentProfiles,
}: EditEventPaymentSettingsFormProps) => {
  const { t } = useTranslation();
  const { getPaymentMethodTitle } = usePaymentMethods();
  const { parseDate, formatCurrency, formatDate } = useLocale();
  const project = useProject();
  const numberInput = useNumberInput();

  const getValues = (event: Event) => ({
    payment_profile: event.payment_profile ? { id: event.payment_profile.id } : null,
    require_company_for_bank_transfer: event.require_company_for_bank_transfer,
    start_company_bank_transfer_after_days: event.start_company_bank_transfer_after_days === 0
      ? null
      : event.start_company_bank_transfer_after_days,
    invoice_prefix: event.invoice_prefix,
    vat_number: event.vat_number,
    allow_down_payments: event.allow_down_payments,
    down_payment_percentage: event.down_payment_percentage,
    down_payment_deadline: event.down_payment_deadline,
    down_payment_tickets: event.tickets.filter((ticket) => ticket.allow_down_payments).map((ticket) => ticket.id),
    down_payment_products: event.products.filter((product) => product.allow_down_payments).map((product) => product.id),
  });

  const form = useForm({
    values: getValues(event),
  });

  useNavigationPrompt(form.changed());

  const [editEvent, { error, loading }] = useEditEventMutation({
    input: {
      id: event.id,
      payments: form.values,
    },
    onSuccess: (event) => {
      form.reset(getValues(event));
    },
  });

  const currentPaymentProfileIsInactive = !!event.payment_profile && paymentProfiles.filter(
    (paymentProfile) => paymentProfile.id === event.payment_profile.id,
  ).length === 0;

  const visiblePaymentProfiles = [
    null,
    ...currentPaymentProfileIsInactive ? [event.payment_profile] : [],
    ...paymentProfiles,
  ];

  const selectedPaymentProfile = visiblePaymentProfiles.filter(
    (paymentProfile) => (
      // If no payment profile is selected, return the default payment profile
      !form.values.payment_profile
        ? paymentProfile?.default
        : paymentProfile?.id === form.values.payment_profile?.id
    ),
  )[0];

  const paymentMethodTitles = selectedPaymentProfile && selectedPaymentProfile.enabled_payment_methods.map(
    (paymentMethod) => getPaymentMethodTitle(paymentMethod),
  );

  const bankTransferEnabled = selectedPaymentProfile && selectedPaymentProfile.enabled_payment_methods.filter(
    (paymentMethod) => paymentMethod === PaymentMethod.BankTransfer,
  ).length > 0;

  const getDownPaymentAmount = (amount: number) => (
    amount * (form.values.down_payment_percentage / 100)
  );

  const getFinalPaymentAmount = (amount: number) => (
    amount - getDownPaymentAmount(amount)
  );

  const eventDate = parseDate(event.start_date, { timezone: 'UTC' });

  const diff = form.values.down_payment_deadline
    ? differenceInDays(
      startOfDay(eventDate),
      parseDate(form.values.down_payment_deadline, { format: 'internal_date' }),
    )
    : 0;

  const errors = getServerErrors(error, 'payments');

  return (
    <UI.Form onSubmit={() => editEvent()}>
      <UI.FormGrid>
        <UI.ServerErrors error={error} />

        <UI.Div>
          <UI.Legend sc={{ mb: 1 }}>{t('payment_profile')}</UI.Legend>
          <UI.InputDescription>
            {t('event_payment_profile_description')}
            <ProtectedSection permissions={[Permission.admin]}>
              {' '}
              <UI.Link to={`/${project.id}/project/payments`}>
                {t('manage_payment_profiles')}
              </UI.Link>
            </ProtectedSection>
          </UI.InputDescription>
        </UI.Div>

        <UI.InputGroup sc={{ valid: !errors['payments.payment_profile'], touched: form.touched.payment_profile }}>
          <UI.InputLabel htmlFor="PaymentProfile">
            {t('payment_profile')}
          </UI.InputLabel>
          <UI.Select
            {...form.select(
              'payment_profile',
              visiblePaymentProfiles.map((profile) => (profile ? { id: profile.id } : null)),
              { key: 'id' },
            )}
            id="PaymentProfile"
          >
            {visiblePaymentProfiles.map((profile, index) => (
              <option {...form.option(index)} key={index}>
                {profile?.title || t('use_default_payment_profile')}
              </option>
            ))}
          </UI.Select>
          <UI.ErrorMessages
            attribute={t('payment_profile')}
            errors={errors['payments.payment_profile']}
          />
        </UI.InputGroup>

        {selectedPaymentProfile && !selectedPaymentProfile.active && (
          <UI.Error>
            <UI.Strong sc={{ block: true }}>
              {t('payment_profile_inactive_warning')}
            </UI.Strong>
            {t('payment_profile_inactive_instruction')}
          </UI.Error>
        )}

        {selectedPaymentProfile?.active && !selectedPaymentProfile.can_create_payments && (
          <UI.Warning>
            <UI.Strong sc={{ block: true }}>
              {t('payment_profile_cannot_create_payments_warning')}
            </UI.Strong>
              {t('payment_profile_cannot_create_payments_instruction')}
          </UI.Warning>
        )}

        {selectedPaymentProfile && (
          <UI.Div>
            <UI.Strong>
              {t('enabled_payment_methods')}
              :
            </UI.Strong>
            {' '}
            {paymentMethodTitles.join(', ') || t('none')}
          </UI.Div>
        )}

        {bankTransferEnabled && (
          <>
            <UI.HR />
            <UI.FormGrid sc={{ columns: ['1fr', '1fr 1fr'] }}>
              <UI.FormGrid>
                <UI.Legend>
                  {t('bank_transfer')}
                </UI.Legend>

                <UI.InputGroup
                  sc={{
                    valid: !errors['payments.require_company_for_bank_transfer'],
                    touched: form.touched.require_company_for_bank_transfer,
                  }}
                >
                  <UI.InputLabel htmlFor="allow_bank_transfer_for">
                    {t('allow_bank_transfer_for')}
                  </UI.InputLabel>
                  <UI.Radio {...form.radio('require_company_for_bank_transfer', false)}>
                    {t('everyone')}
                  </UI.Radio>
                  <UI.Radio {...form.radio('require_company_for_bank_transfer', true)}>
                    {t('companies_only')}
                  </UI.Radio>
                  <UI.ErrorMessages
                    attribute={t('allow_bank_transfer_for')}
                    errors={errors['payments.require_company_for_bank_transfer']}
                  />
                </UI.InputGroup>

                <UI.InputGroup
                  sc={{
                    valid: !errors['payments.start_company_bank_transfer_after_days'],
                    touched: form.touched.start_company_bank_transfer_after_days,
                  }}
                >
                  <UI.InputLabel htmlFor="start_company_bank_transfer_after_days">
                    {t('start_company_bank_transfer_after_days')}
                  </UI.InputLabel>
                  <UI.InputDescription>
                    {t('start_company_bank_transfer_after_days_description')}
                  </UI.InputDescription>
                  <UI.InputWrapper>
                    <UI.Input
                      {...form.text('start_company_bank_transfer_after_days',
                        { inputHandler: numberInput({ min: 0, max: 365, decimals: 0 }) })}
                      id="start_company_bank_transfer_after_days"
                      placeholder="0"
                    />
                    <UI.Span>
                      {t('days_lower', { count: form.values.start_company_bank_transfer_after_days || 0 })}
                    </UI.Span>
                  </UI.InputWrapper>
                  <UI.ErrorMessages
                    attribute={t('start_company_bank_transfer_after_days')}
                    errors={errors['payments.start_company_bank_transfer_after_days']}
                  />
                </UI.InputGroup>
              </UI.FormGrid>
            </UI.FormGrid>
          </>
        )}

        <UI.HR />

        <UI.Div>
          <UI.Legend sc={{ mb: 1 }}>{t('invoices')}</UI.Legend>
          <UI.InputDescription>
            {t('invoice_prefix_description')}
          </UI.InputDescription>
        </UI.Div>

        <UI.FormGrid sc={{ columns: ['1fr', '1fr 1fr'] }}>
          <UI.InputGroup
            sc={{ valid: !errors['payments.invoice_prefix'], touched: form.touched.invoice_prefix }}
          >
            <UI.InputLabel htmlFor="InvoicePrefix">
              {t('invoice_prefix')}
            </UI.InputLabel>
            <UI.DebouncedInput
              {...form.text('invoice_prefix')}
              id="InvoicePrefix"
              match={/[0-9A-Za-z-]/g}
              maxLength={20}
            />
            <UI.ErrorMessages
              attribute={t('invoice_prefix')}
              errors={errors['payments.invoice_prefix']}
            />
          </UI.InputGroup>
          <UI.Info>
            <UI.Strong sc={{ block: true }}>
              {t('example')}
            </UI.Strong>
            #
            {form.values.invoice_prefix ? `${form.values.invoice_prefix}-` : ''}
            1234
          </UI.Info>
        </UI.FormGrid>

        <UI.HR />

        <UI.Div>
          <UI.Legend sc={{ mb: 1 }}>{t('vat_number')}</UI.Legend>
          <UI.InputDescription>
            {t('vat_number_on_event_description')}
          </UI.InputDescription>
        </UI.Div>

        <UI.FormGrid sc={{ columns: '1fr 1fr' }}>
          <UI.InputGroup sc={{ valid: !errors['payments.vat_number'], touched: form.touched.vat_number }}>
            <UI.InputLabel htmlFor="VatNumber">
              {t('vat_number')}
            </UI.InputLabel>
            <UI.DebouncedInput
              {...form.text('vat_number')}
              id="VatNumber"
              placeholder={project.organisation_vat_number}
            />
            <UI.ErrorMessages
              attribute={t('vat_number')}
              errors={errors['payments.vat_number']}
            />
          </UI.InputGroup>
        </UI.FormGrid>

        <UI.HR />

        <UI.Div>
          <UI.Legend sc={{ mb: 1 }}>
            {t('down_payments')}
          </UI.Legend>
          <UI.InputDescription>
            {t('edit_down_payment_settings_description')}
          </UI.InputDescription>
        </UI.Div>

        <UI.InputGroup
          sc={{ valid: !errors['payments.allow_down_payments'], touched: form.touched.allow_down_payments }}
        >
          <UI.InputLabel>
            {t('down_payments')}
          </UI.InputLabel>
          <UI.FormGrid sc={{ gutter: 0.25 }}>
            <UI.Checkbox
              {...form.checkbox('allow_down_payments')}
              checked={form.values.allow_down_payments}
            >
              {t('allow_down_payments')}
            </UI.Checkbox>
            <UI.ErrorMessages attribute={t('allow_down_payments')} errors={errors['payments.allow_down_payments']} />
          </UI.FormGrid>
        </UI.InputGroup>

        {form.values.allow_down_payments && (
          <UI.FadeIn>
            <UI.FormGrid>
              <UI.FormGrid sc={{ columns: ['1fr', '1fr 1fr'] }}>
                <UI.InputGroup
                  sc={{ valid: !errors['payments.down_payment_percentage'],
                    touched: form.touched.down_payment_percentage }}
                >
                  <UI.InputLabel htmlFor="down_payment_percentage">
                    {t('down_payment_percentage')}
                    {' '}
                    <UI.RequiredMark />
                  </UI.InputLabel>
                  <UI.InputDescription>
                    {t('down_payment_percentage_description')}
                  </UI.InputDescription>
                  <UI.InputWrapper>
                    <UI.Input
                      {...form.text('down_payment_percentage',
                        { inputHandler: numberInput({ min: 0, max: 100, decimals: 0 }) })}
                      id="down_payment_percentage"
                      placeholder="0"
                    />
                    <span>%</span>
                  </UI.InputWrapper>
                  <UI.ErrorMessages
                    attribute={t('down_payment_percentage')}
                    errors={errors['payments.down_payment_percentage']}
                  />
                </UI.InputGroup>
              </UI.FormGrid>

              <UI.FormGrid sc={{ columns: ['1fr', '1fr 1fr'] }}>
                <UI.InputGroup
                  sc={{ valid: !errors['payments.down_payment_deadline'], touched: form.touched.down_payment_deadline }}
                >
                  <UI.InputLabel htmlFor="down_payment_deadline">
                    {t('down_payment_deadline')}
                    {' '}
                    <UI.RequiredMark />
                  </UI.InputLabel>
                  <UI.InputDescription>
                    {t('down_payment_deadline_description')}
                  </UI.InputDescription>
                  <UI.DatePicker
                    id="down_payment_deadline"
                    selected={form.values.down_payment_deadline}
                    onChange={(value) => form.set('down_payment_deadline', value)}
                    selectsStart
                    startDate={form.values.down_payment_deadline}
                  />
                  <UI.ErrorMessages
                    attribute={t('down_payment_deadline')}
                    errors={errors['payments.down_payment_deadline']}
                  />
                </UI.InputGroup>
                <UI.Div>
                  <UI.Info>
                    <Trans
                      i18nKey="your_event_takes_place"
                      values={{ date: formatDate(
                        eventDate,
                        { format: 'display_compact_date' },
                      ) }}
                    >
                      <UI.Strong />
                    </Trans>
                    {' '}
                    {diff !== 0 && (
                      <Trans
                        i18nKey={`down_payment_deadline_diff_${diff > 0 ? 'before' : 'after'}`}
                        values={{ count: Math.abs(diff) }}
                      >
                        <UI.Strong />
                      </Trans>
                    )}
                    {' '}
                    {t('down_payment_deadline_diff_reminder')}
                  </UI.Info>
                </UI.Div>
              </UI.FormGrid>

              <UI.InputGroup
                sc={{ valid: !errors['payments.down_payment_tickets'], touched: form.touched.down_payment_tickets }}
              >
                <UI.InputLabel>
                  {t('eligible_tickets')}
                </UI.InputLabel>
                {event.tickets.map((ticket) => (
                  <UI.Checkbox
                    {...form.checklist('down_payment_tickets', ticket.id)}
                    key={ticket.id}
                  >
                    {ticket.title}
                    {' '}
                    {form.values.down_payment_percentage > 0 && (
                    <UI.Span sc={{ muted: true }}>
                      (
                      {formatCurrency(
                        getDownPaymentAmount(ticket.standard_promotion.amount),
                      )}
                      {' + '}
                      {formatCurrency(
                        getFinalPaymentAmount(ticket.standard_promotion.amount),
                      )}
                      )
                    </UI.Span>
                    )}
                  </UI.Checkbox>
                ))}
                {event.tickets.length === 0 && (
                  <Trans i18nKey="no_tickets_created">
                    <UI.Link to={`/${project.id}/events/${event.id}/tickets`} />
                  </Trans>
                )}
                <UI.ErrorMessages attribute={t('tickets')} errors={errors['payments.tickets']} />
              </UI.InputGroup>

              <UI.InputGroup
                sc={{ valid: !errors['payments.down_payment_products'], touched: form.touched.down_payment_products }}
              >
                <UI.InputLabel>
                  {t('eligible_products')}
                </UI.InputLabel>
                {event.products.map((product) => (
                  <UI.Checkbox
                    {...form.checklist('down_payment_products', product.id)}
                    key={product.id}
                  >
                    {product.title}
                    {' '}
                    {form.values.down_payment_percentage > 0 && (
                    <UI.Span sc={{ muted: true }}>
                      (
                      {formatCurrency(
                        getDownPaymentAmount(product.standard_promotion.amount),
                      )}
                      {' + '}
                      {formatCurrency(
                        getFinalPaymentAmount(product.standard_promotion.amount),
                      )}
                      )
                    </UI.Span>
                    )}
                  </UI.Checkbox>
                ))}
                {event.products.length === 0 && (
                  <Trans i18nKey="no_products_created">
                    <UI.Link to={`/${project.id}/events/${event.id}/products/add`} />
                  </Trans>
                )}
                <UI.ErrorMessages attribute={t('products')} errors={errors['payments.products']} />
              </UI.InputGroup>
            </UI.FormGrid>
          </UI.FadeIn>
        )}

        <UI.FormButtons
          form={form}
          submitLabel={t('save_event')}
          loading={loading}
        />
      </UI.FormGrid>
    </UI.Form>
  );
};

export default EditEventPaymentSettingsForm;
